import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Pagination } from "@/components/ui/pagination";
import PageHeader from "../PageHeader";
import usePageParam from "../../hooks/usePageParam";
import { snippetApi } from "../../services/api";
import { SNIPPET_TYPE } from "../../constants";
import AuthContext from "../../context/AuthContext";
import { isAgent } from "../tasks/userDisplay";
import { VISIBILITY_LABELS } from "../sharing/visibility";

const PAGE_SIZE = 12; // mirrors backend REST_FRAMEWORK PAGE_SIZE

function SnippetList() {
  // Snippets are library-group scoped: an owner and their agents share one
  // store. Rows you did not write have to say so, or an agent's scratch
  // note is indistinguishable from your own.
  const auth = useContext(AuthContext);
  const me = auth?.user?.user_id ?? null;
  const [snippets, setSnippets] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = usePageParam();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSnippets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page };
      if (search) params.search = search;
      if (typeFilter) params.snippet_type = typeFilter;
      const response = await snippetApi.getAll(params);
      setSnippets(response.data.results || []);
      setCount(response.data.count || 0);
    } catch (err) {
      // A page past the end (snippets deleted since, or an old link).
      if (err?.response?.status === 404 && page > 1) {
        setPage(1, { replace: true });
        return;
      }
      setError(err?.message || "Failed to load snippets");
    } finally {
      setLoading(false);
    }
  }, [page, setPage, search, typeFilter]);

  useEffect(() => {
    fetchSnippets();
  }, [fetchSnippets]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchInput);
    setPage(1, { replace: true });
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1, { replace: true });
  };

  const handleTypeChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(1, { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="page-container mt-6 pt-4">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Snippets"
          subtitle="Private store for text, markdown, and code"
        />
        <Button asChild className="shrink-0">
          <Link to="/snippets/new">New snippet</Link>
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form onSubmit={handleSearchSubmit} className="min-w-0 flex-1">
          <div className="flex">
            <Input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search title or body…"
              aria-label="Search snippets"
              className="rounded-r-none"
            />
            <Button
              type="submit"
              variant="outline"
              className="rounded-l-none border-l-0"
            >
              Search
            </Button>
            {search && (
              <Button
                type="button"
                variant="outline"
                className="rounded-l-none border-l-0"
                onClick={handleClearSearch}
              >
                Clear
              </Button>
            )}
          </div>
        </form>
        <NativeSelect
          value={typeFilter}
          onChange={handleTypeChange}
          className="w-auto"
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          <option value={SNIPPET_TYPE.PLAIN}>Plain text</option>
          <option value={SNIPPET_TYPE.MARKDOWN}>Markdown</option>
          <option value={SNIPPET_TYPE.CODE}>Code</option>
        </NativeSelect>
      </div>

      {loading && (
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading snippets...</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              Failed to load snippets. Please try again.
            </span>
            <Button size="sm" variant="outline" onClick={fetchSnippets}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && !error && snippets.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          {search || typeFilter
            ? "No snippets match those filters."
            : "No snippets yet. Create one to get started."}
        </div>
      )}

      {!loading && !error && snippets.length > 0 && (
        <div className="flex flex-col gap-2">
          {snippets.map((snippet) => (
            <Link
              key={snippet.slug}
              to={`/snippets/${snippet.slug}`}
              className="block rounded-lg border bg-card p-4 text-card-foreground shadow-xs transition-shadow hover:shadow-md"
            >
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <h3 className="mb-0 min-w-0 truncate font-display text-base font-semibold">
                  {snippet.title || snippet.slug}
                </h3>
                <div className="flex shrink-0 items-center gap-2">
                  {snippet.user && me != null && snippet.user.id !== me && (
                    <span className="text-sm text-muted-foreground">
                      by {snippet.user.username}
                      {isAgent(snippet.user) && " (agent)"}
                      {snippet.can_edit === false && " · read + copy"}
                    </span>
                  )}
                  {snippet.visibility && snippet.visibility !== "private" && (
                    <>
                      {" "}
                      <Badge variant="default" className="font-normal">
                        {VISIBILITY_LABELS[snippet.visibility]}
                      </Badge>
                    </>
                  )}
                  <Badge variant="secondary" className="font-normal">
                    {snippet.snippet_type}
                  </Badge>
                  {snippet.language && (
                    <>
                      {" "}
                      <Badge variant="outline" className="font-normal">
                        {snippet.language}
                      </Badge>
                    </>
                  )}{" "}
                  <span className="text-sm text-muted-foreground">
                    {new Date(snippet.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {snippet.title && (
                <div className="mb-1 font-mono text-sm text-muted-foreground">
                  {snippet.slug}
                </div>
              )}
              <p className="mb-0 line-clamp-2 whitespace-pre-wrap break-words text-muted-foreground">
                {snippet.body}
              </p>
            </Link>
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <Pagination
          page={page}
          total={totalPages}
          onChange={setPage}
          className="mt-4"
        />
      )}
    </div>
  );
}

export default SnippetList;
